import * as React from 'react';
import { Button, Col } from 'react-bootstrap';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { orderBy } from 'lodash';
import { constants } from '../../constants/constants';
import { Ifacility, Ioption } from '../../models';
import { getFacilities } from '../../reducers/facilitiesReducer';
import { getProductInfo } from '../../reducers/productInfoReducer';
import CommonModal from '../common/CommonModal';

interface Iprops {
    showModal: boolean;
    hideModal: () => void;
    selection: string[];
    colorButton: string;
    assignStandard: (
        facilityIDs: string[],
        standardID: string
    ) => Promise<void>;
}

/*
 * Bulk "Assign Standard" (MMG-4353).
 * A facility carries a single required standardID, so this is a single-select
 * list rather than checkboxes - picking one standard overwrites whatever each
 * selected facility had, which is what the warning above the list says.
 */
const AssignStandardModal: React.FC<Iprops & WithTranslation> = props => {
    const { t } = props;
    const allFacilities = useSelector(getFacilities);
    const productInfo = useSelector(getProductInfo);
    const [selectedStandardID, setSelectedStandardID] = React.useState('');
    const [isAssigning, setIsAssigning] = React.useState(false);

    // 'select-<id>' is the key format react-table's selectTable HOC hands back.
    const facilityIDs = React.useMemo(
        () => props.selection.map(value => value.slice(7)),
        [props.selection]
    );

    const facilities: Ifacility[] = React.useMemo(
        () =>
            facilityIDs
                .map(id => allFacilities[id])
                .filter((facility): facility is Ifacility => Boolean(facility)),
        [facilityIDs, allFacilities]
    );

    const standardOptions: Ioption[] = React.useMemo(
        () =>
            orderBy(productInfo.standardOptions, option =>
                option.label.toLowerCase()
            ),
        [productInfo.standardOptions]
    );

    // Start each open with nothing selected so a standard is never applied by
    // a stray double click on the menu item.
    React.useEffect(() => {
        if (props.showModal) {
            setSelectedStandardID('');
            setIsAssigning(false);
        }
    }, [props.showModal]);

    const assign = async () => {
        if (selectedStandardID === '') {
            toastr.warning(
                t('warning'),
                t('selectAStandard'),
                constants.toastrWarning
            );
            return;
        }

        // Guard the second click: this writes to every selected facility, so a
        // double submit would fire the whole bulk update twice.
        if (isAssigning) {
            return;
        }

        setIsAssigning(true);

        try {
            await props.assignStandard(facilityIDs, selectedStandardID);
        } finally {
            // The parent closes this modal on success; on failure it stays open
            // so the user can retry without re-picking their selection.
            setIsAssigning(false);
        }
    };

    return (
        <CommonModal
            show={props.showModal}
            className="user-edit"
            onHide={() => props.hideModal}
            title={t('assignStandard')}
            container={document.getElementById('two-pane-layout')}
        >
            <div style={{ padding: '0 15px' }}>
                <p
                    style={{
                        color: constants.colors.red,
                        fontWeight: 'bold'
                    }}
                >
                    {t('assignStandardWarning', {
                        count: facilities.length
                    })}
                </p>

                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                    {facilities.map(facility => (
                        <li key={facility.id}>
                            {facility.name}
                            {' — '}
                            <span style={{ color: constants.colors.textBlue }}>
                                {productInfo?.standards?.[facility.standardID]
                                    ?.name || t('common:noStandardAssigned')}
                            </span>
                        </li>
                    ))}
                </ul>

                <div style={{ marginBottom: '20px' }}>
                    {standardOptions.map(option => (
                        <div key={option.value} style={{ marginBottom: '6px' }}>
                            <label style={{ fontWeight: 'normal' }}>
                                <input
                                    type="radio"
                                    name="assign-standard"
                                    value={option.value}
                                    checked={
                                        selectedStandardID === option.value
                                    }
                                    disabled={isAssigning}
                                    onChange={(
                                        event: React.ChangeEvent<
                                            HTMLInputElement
                                        >
                                    ) =>
                                        setSelectedStandardID(
                                            event.target.value
                                        )
                                    }
                                    style={{ marginRight: '10px' }}
                                />
                                {option.label}
                            </label>
                        </div>
                    ))}
                </div>

                <Col xs={12} className="form-buttons text-right">
                    <Button
                        bsStyle="default"
                        type="button"
                        className="pull-left"
                        onClick={props.hideModal}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        bsStyle={props.colorButton}
                        disabled={isAssigning}
                        onClick={assign}
                    >
                        {t('assign')}
                    </Button>
                </Col>
            </div>
        </CommonModal>
    );
};

export default withTranslation('manageFacility')(AssignStandardModal);
